import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import { Divider } from 'inpock-ui-react';

import { Icon } from '@/components';
import { MarketHeader, MarketCampaigns, MarketReviews, MarketFooterV2 } from '@/fragments';
import { useMarketCampaignsV2Query, useMarketV2Query } from '@/hooks/queries/market';
import { MarketProduct as MarketProductType } from '@/apis';
import { useCartCountQuery } from '@/hooks';

import * as S from './styled';

export function MarketV2Page() {
  const { username } = useParams();
  const { data: cartCountData } = useCartCountQuery();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const [isTop, setIsTop] = useState<boolean>(true);

  const { data: marketData, isLoading: isMarketLoading } = useMarketV2Query(username as string);
  const [allCampaigns, setAllCampaigns] = useState<MarketProductType[]>([]);
  const {
    data: campaignsData,
    isLoading: isProductLoading,
    fetchNextPage,
  } = useMarketCampaignsV2Query(username as string, activeCategory, isIntersecting);

  const allCampaignRef = useRef<HTMLDivElement>(null);

  const onClickCategory = (category: string) => {
    setActiveCategory(category);
  };

  const onClickShowMore = () => {
    fetchNextPage();
  };

  const moveToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScroll = useCallback(() => {
    if (window.scrollY > 0) {
      setIsTop(false);
    } else {
      setIsTop(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (allCampaignRef.current) {
      observer.observe(allCampaignRef.current);
    }

    return () => {
      if (allCampaignRef.current) {
        observer.unobserve(allCampaignRef.current);
      }
    };
  }, [isMarketLoading]);

  useEffect(() => {
    if (campaignsData) {
      const products = campaignsData.pages.flatMap((page) => page.results);
      setAllCampaigns(products);
    }
  }, [campaignsData]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{`${username}님의 마켓`}</title>
          <meta name="description" content={`${username}님의 마켓`} />
          <meta property="og:description" content={`${username}님의 마켓`} />
          <meta property="og:title" content={`${username}님의 마켓`} />
          <meta property="og:url" content={window.location.href} />
        </Helmet>
      </HelmetProvider>
      <S.MarketView>
        <S.ToTopButton onClick={moveToTop} className={isTop ? 'hide' : 'show'}>
          <Icon icon="ArrowUpLight" size={16} />
        </S.ToTopButton>
        <div className="container">
          {/* 헤더 영역 */}
          <MarketHeader
            info={{
              username,
              notice: marketData?.market_setting.notice_text || '',
              images: marketData?.market_banners.map((banner) => banner.image),
              loading: isMarketLoading,
              logoText: marketData?.market_setting.logo_text,
              logoImage: marketData?.market_setting.logo_image,
              logoType: marketData?.market_setting.logo_type,
              cartCount: cartCountData?.cart_count || 0,
            }}
            version="v2"
          />
          {/* 판매중인 상품 */}
          {marketData?.campaigns.length !== 0 && (
            <S.StyledSection>
              <MarketCampaigns.ForSale
                products={marketData?.campaigns}
                loading={isMarketLoading}
                marketId={marketData?.id}
                version="v2"
              />
            </S.StyledSection>
          )}
          <Divider style={{ marginTop: '20px' }} wide />
          {/* 전체 상품 섹션 */}
          {!isMarketLoading && (
            <S.StyledSection ref={allCampaignRef}>
              <MarketCampaigns.All
                categories={marketData?.categories}
                onClickTab={onClickCategory}
                activeCategory={activeCategory}
                products={allCampaigns}
                currentPage={campaignsData?.pages[campaignsData?.pages.length - 1].current_page}
                totalPage={campaignsData?.pages[0].total_page}
                onClickShowMore={onClickShowMore}
                loading={{
                  market: isMarketLoading,
                  products: isProductLoading,
                }}
                marketId={marketData?.id}
                version="v2"
              />
            </S.StyledSection>
          )}
          {/* 리뷰 섹션 */}
          {marketData?.market_setting.is_review_visible && (
            <>
              <Divider wide />
              <S.StyledSection>
                <MarketReviews
                  reviews={marketData?.reviews}
                  loading={isMarketLoading}
                  version="v2"
                />
              </S.StyledSection>
            </>
          )}
          {/* 푸터 영역 */}
          {!isMarketLoading && marketData && <MarketFooterV2 info={marketData?.market_info} />}
        </div>
      </S.MarketView>
    </>
  );
}
